import { assert } from '@ember/debug';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { STATUS_KEYS } from 'scaleflow/models/initiative-update';
import textDeltaIsEmpty from 'scaleflow/utils/text-delta-is-empty';
import Quill from 'quill';
import registerIfNotRegistered from 'scaleflow/utils/quill-extenstions/registerIfNotRegistered';
import ImageUploader from 'quill2-image-uploader';
import ImageFormat from 'scaleflow/utils/quill-extenstions/image-format';
import imageResizeOptions from 'scaleflow/utils/quill-extenstions/image-resize';
import imageUploadHandler from 'scaleflow/utils/quill-extenstions/image-uploader';

Quill.register('formats/image', ImageFormat, true);
registerIfNotRegistered(Quill, 'modules/imageUploader', ImageUploader);

export default class InitiativesUpdateWizardGeneralRemarksComponent extends Component {
  @service betaToggle;
  @service store;
  @service currentUser;
  @service session;
  @service organisation;
  @service intl;

  @tracked healthScore =
    this.args.initiative?.latestUpdate?.reported_health ?? undefined;
  @tracked healthStatus =
    this.args.initiative?.latestUpdate?.status ?? STATUS_KEYS.ON_TRACK;
  @tracked remarks = undefined;

  @tracked activeStep = 0;
  @tracked isSaving = false;

  @tracked update = {};

  @action
  setActiveStep(step) {
    this.activeStep = step;
  }

  @action
  setHealthScore(e) {
    this.healthScore = e;
  }

  @action
  setHealthStatus(health) {
    this.healthStatus = health.value;
  }

  @action
  setRemarks(e) {
    this.remarks = e;
  }

  @action
  hideEditor() {
    this.editorIsVisible = false;
  }

  @action
  cancel() {
    this.hideEditor();
  }

  @action
  resetForm() {
    this.healthScore = undefined;
    this.healthStatus = null;
    this.remarks = undefined;
    this.update = '';
    // this.hideEditor();
  }

  get isDirty() {
    return (
      !!this.healthStatus ||
      !!this.healthScore ||
      !textDeltaIsEmpty(this.remarks)
    );
  }

  @action
  async saveForm() {
    this.isSaving = true;
    await this.save()
      .catch((err) => {
        this.isSaving = false;
        throw err;
      })
      .finally(() => {
        this.isSaving = false;
      });
    this.next();
  }

  @action
  async save() {
    if (
      (!this.healthStatus &&
        !this.healthScore &&
        textDeltaIsEmpty(this.remarks)) ||
      !this.args.initiative
    )
      return;
    this.update = this.store.createRecord('initiative-update');
    this.update.initiative = this.args.initiative;
    this.update.initiative_id = this.args.initiative.id;
    this.update.status = this.healthStatus;
    this.update.reported_health = this.healthScore;
    this.update.text_delta = this.remarks;
    this.update.created_at = new Date().toISOString();
    this.update.updated_by = { type: 'PERSON', id: this.currentUser.id };
    await this.update.save();
    this.remarks = undefined;
    this.args.initiative.progress = this.update.status;
    this.hideEditor();
  }

  @action
  next() {
    if (!this.args?.next)
      assert('Pass a callback function to the "next" param');
    else this.args?.next();
  }

  get modules() {
    const modules = {};

    modules.imageUploader = {
      upload: (file) => {
        return imageUploadHandler(
          this.session.idToken,
          this.organisation.id,
          file,
        ).then((s3Link) => {
          return s3Link;
        });
      },
    };

    modules.resize = imageResizeOptions(this.intl);

    let toolbarOptions = [
      ['bold', 'italic', 'strike'], // toggled buttons
      ['blockquote', 'code-block'],

      [{ header: 1 }, { header: 2 }], // custom button values
      [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }],
      ['link', 'image', 'clean'], // remove formatting button
    ];

    modules.toolbar = toolbarOptions;

    return modules;
  }

  get keyBindings() {
    const that = this;
    return {
      submit: {
        key: 13, // Enter key code
        shortKey: true, // For cmd key
        handler: function () {
          that.save();
        },
      },
    };
  }

  @action
  handleCancel() {
    this.resetForm();
    this.args?.onCancel?.();
  }
}
